import { useCallback, useEffect, useMemo, useState } from 'react';
import { SButton, SQuadrantLayout, SText } from '@setvi/shared/components';
import { Box, makeStyles, Dialog } from '@material-ui/core';
import { enqueueSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { useDialog } from '@setvi/shared/providers';
import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import {
  sendMessageToSlackChannel,
  getCustomerByUniqueValue
} from '@setvi/shared/services';
import { Customer } from 'Services/Mongo/Types';
import {
  customerAccountNumber,
  priceGroupId,
  useAlgoliaProducts
} from 'Hooks/useAlgoliaProducts';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  IntelligenceIcon,
  ThumbsUpIcon
} from '@setvi/shared/components/sicons';
import { SlackMessageTypes } from '@setvi/shared/enums';
import {
  PriceGroup,
  Product,
  ProductDetailsTabsIds,
  ProductPriceType
} from '@setvi/shared/interfaces';
import { useSearchParams } from 'react-router-dom';
import {
  AnalyticsAppType,
  AnalyticsModules,
  useAnalytics,
  useMixpanel,
  useSelected
} from '@setvi/shared/hooks';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useComposeEmail } from 'providersV2/compose-email/context';
import { ProductDetails } from 'componentsV2/product/details';
// import { AIButton } from 'componentsV2';
import Feedback, {
  FEEDBACK_DIALOG,
  FeedbackValues
} from 'componentsV2/ai/feedback';

import { SearchFormOptions } from '@setvi/shared/components/intelligence/product-match/enums';
import { SearchType } from '@setvi/shared/components/intelligence/search/interfaces';
import { useCatalogStore } from '@setvi/shared/stores';
import { distances } from '@setvi/shared/styles';
import { getPriceGroup } from 'Services/Query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { COMPANIES } from 'Enums/Companies';
import { Header } from './components/header';
import {
  LIMIT_SELECTED_PRODUCTS,
  ProductActionType,
  appendPricingToProducts,
  getNumOfProductsInComposeMail,
  parseKeyValueArrayToObject,
  parseProductsToEmailProduct
} from './utils';
import { useCatalogConfig } from './hooks/useCatalogConfig';
import { PromotionDialog } from './components/promotion';
import { AskAiButton, FeedbackButton, FeedbackContainer } from './styled';
import {
  ProductsList,
  InfoContainer,
  BottomLeftWrapper,
  AIProductsFilters,
  InvalidConfiguration
} from './components';
import { useAISearchResults } from './hooks/use-ai-search-result';
import { GetPricesProps, PriceType } from './api';
import { PriceLister } from './components/price-lister';
import { MobileCatalog } from './mobile';

export const useProductStyles = makeStyles({
  filtersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid #e0e0e0`,
    height: 55
  },
  filterConteiner: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  dialogPaper: {
    width: '95%',
    height: '95%',
    maxHeight: '95% !important',
    maxWidth: '95% !important',
    borderRadius: 24
  }
});

interface ProductsProps {
  mode?: 'standalone' | 'embedded';
  type?: 'none' | 'checkbox' | 'radio';
  appType?: AnalyticsAppType;
  selectedProducts?: Product[];
  selectedCustomer?: Customer;
  selectedFilters?: string;
  visibleFacets?: string[];
  canShowActionBar?: boolean;
  onEmail?: (products: Product[]) => void;
  handleExport?: (products: Product[]) => void;
}

export default function Products({
  onEmail,
  handleExport,
  mode = 'standalone',
  type = 'checkbox',
  selectedProducts,
  appType,
  selectedFilters: importedFilters,
  selectedCustomer: importedCustomer,
  canShowActionBar = true,
  visibleFacets
}: ProductsProps) {
  const { track } = useMixpanel();

  useEffect(() => {
    track({ name: 'Page View', props: { page: 'Catalog' } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchType, setSearchType] = useState<SearchType>(SearchType.Regular);
  const [dialogOpened, setDialogOpened] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [defaultTab, setDefaultOpenedTab] =
    useState<ProductDetailsTabsIds>(null);
  const flags = useFlags();
  // const headerSearchInputRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer>(
    importedCustomer || null
  );
  const [selectedPriceGroup, setSelectedPriceGroup] =
    useState<PriceGroup>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { companyData, user: appUser } = useAppContext();
  const userType = companyData.Settings[CompanySettingsTypeID.CompanyType]
    ?.Value as CompanyProductUserType;
  const { configuration, invalidConfiguration } = useCatalogConfig();
  const { values, open } = useComposeEmail();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const isFullPrice = useMemo(
    () => configuration?.price === ProductPriceType.Full,
    [configuration?.price]
  );
  const isGetFullPrice = useMemo(
    () => configuration?.price === ProductPriceType.GetFull,
    [configuration?.price]
  );

  const { mutateAsync: sendFeedback, isLoading: isFeedbackLoading } =
    useMutation(sendMessageToSlackChannel());

  const {
    facets,
    searchQuery,
    selectedFacets,
    selectedCategory,
    attributesForFaceting,
    isLoading,
    fetchMoreLoading,
    productPricesLoading,
    products: algoliaProducts,
    getPrices,
    searchProducts,
    fetchMore: fetchMoreProducts,
    fetchProductsByCategory,
    setSelectedCategory,
    onSelectFacet,
    setProducts,
    setProductPricesLoading,
    handleUpdateAlgoliaProduct,
    refetchProducts,
    setSearchQuery
  } = useAlgoliaProducts({
    selectedCustomer,
    selectedPriceGroup,
    userType,
    appType,
    importedFilters,
    canFetchPrices: isFullPrice
  });
  const {
    aiProducts,
    aiFilteredProducts,
    aiSearchLoading,
    aiSearch,
    aiType,
    selectedFilters,
    urlDescription,
    controller,
    statusId,
    setAiProducts,
    setAISearch,
    handleFilter,
    handleAISearch
  } = useAISearchResults({
    selectedCustomer,
    showStockFilter: configuration?.product?.grid?.stock?.show
  });

  const { openDialog, closeDialog } = useDialog();
  const { mutateAsync: getCustomer } = useMutation(getCustomerByUniqueValue());
  const { captureAnalytics } = useAnalytics();
  // const { onProductEnhancerMenuItemClick } = useProductEnhancer();
  const classes = useProductStyles();

  const keyGetter = (product: Product) => product?._id;
  const { selected, handleSelect, modifySelected, setSelected } =
    useSelected<Product>({
      dataKeyGetter: keyGetter,
      preselected: selectedProducts || []
    });

  // Sync selected state when selectedProducts prop changes from parent
  useEffect(() => {
    if (selectedProducts) {
      setSelected(selectedProducts);
    }
  }, [selectedProducts, setSelected]);

  const numOfProductsInComposeMail = useMemo(
    () => getNumOfProductsInComposeMail(values),
    [values]
  );

  const disableSelect =
    numOfProductsInComposeMail + selected.length >= LIMIT_SELECTED_PRODUCTS;

  const aiSearchActive = useMemo(
    () => searchType === SearchType.AI,
    [searchType]
  );

  const { setIsAISearchActive } = useCatalogStore();

  const handlePromotion = useCallback(
    (product: Product) => {
      openDialog(<PromotionDialog product={product} onClose={closeDialog} />);
    },
    [openDialog, closeDialog]
  );

  const updateFullPrice = async (props: GetPricesProps) => {
    setProductPricesLoading(true);

    const prices = await getPrices(props);

    if (!aiSearchActive) {
      setProducts(prevProducts =>
        appendPricingToProducts(prevProducts, prices)
      );
    } else {
      setAiProducts(prevProducts =>
        appendPricingToProducts(prevProducts, prices)
      );
    }

    setProductPricesLoading(false);
  };

  const handleSelectCustomer = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setSelectedPriceGroup(null);
    searchParams.delete(priceGroupId);

    if (customer) {
      const analyticsFilters = parseKeyValueArrayToObject(selectedFacets);

      captureAnalytics(
        {
          ...(selectedFacets?.length && { filters: analyticsFilters }),
          customerId: customer?._id,
          actionType: ProductActionType.CustomerSelection
        },
        AnalyticsModules.Catalog
      );

      searchParams.set(customerAccountNumber, customer?.accountNumber);

      if (isFullPrice) {
        updateFullPrice({
          accountNumber: customer.accountNumber,
          productList: aiSearchActive ? aiFilteredProducts : algoliaProducts
        });
      }

      if (isGetFullPrice) {
        refetchProducts();
      }
    } else searchParams.delete(customerAccountNumber);

    setSearchParams(searchParams);
  };

  const handleSelectPriceGroup = (priceGroup: PriceGroup) => {
    setSelectedPriceGroup(priceGroup);
    setSelectedCustomer(null);
    searchParams.delete(customerAccountNumber);

    if (priceGroup) {
      searchParams.set(priceGroupId, priceGroup?._id);
      if (isFullPrice) {
        updateFullPrice({
          productList: aiSearchActive ? aiFilteredProducts : algoliaProducts,
          accountNumber: priceGroup._id,
          type: PriceType.PriceGroup
        });
      }
    } else searchParams.delete(priceGroupId);

    setSearchParams(searchParams);
  };

  const handleEmailSelectedProducts = useCallback(
    (productsToEmail: Product[]) => {
      captureAnalytics(
        {
          productSKU: productsToEmail.map(p => p.sku).join(','),
          actionType: ProductActionType.AddProductToEmail
        },
        AnalyticsModules.Catalog
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const emailProducts = (products: Product[]) => {
    if (onEmail) onEmail(products);
    else open({ products: parseProductsToEmailProduct(products) });
  };

  const handleEmail = () => {
    emailProducts(selected);

    handleSelect(false, selected);
    handleEmailSelectedProducts?.(selected);
    setDrawerOpen(false);
    setDialogOpened(false);
  };

  useEffect(() => {
    const accountNumber = searchParams.get(customerAccountNumber);
    if (
      accountNumber &&
      // Check if the customer is already selected to avoid unnecessary API calls
      selectedCustomer?.accountNumber !== accountNumber
    ) {
      getCustomer({ uniqueValue: accountNumber }).then(customer => {
        if (customer) setSelectedCustomer(customer);
      });
    }
  }, [getCustomer, searchParams, selectedCustomer]);

  useEffect(() => {
    const id = searchParams.get(priceGroupId);
    if (
      id &&
      // Check if the selected group is already selected to avoid unnecessary API calls
      selectedPriceGroup?._id !== id &&
      configuration?.price === ProductPriceType.Full
    ) {
      getPriceGroup(id).then(response => {
        if (response?._id) {
          setSelectedPriceGroup(response);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  const openProductDetails = (
    product: Product,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => {
    setSelectedProduct(product);
    setDialogOpened(true);
    setDefaultOpenedTab(defaultOpenedTab);

    track({
      name: 'Details',
      props: {
        page: 'Catalog',
        id: product?._id,
        sku: product?.sku,
        name: product?.name,
        tab: defaultOpenedTab
      }
    });

    captureAnalytics(
      {
        productSKU: product.sku,
        appType,
        actionType: ProductActionType.DetailsView,
        ...(selectedCustomer
          ? {
              customerId: selectedCustomer._id
            }
          : {})
      },
      AnalyticsModules.Catalog
    );
  };

  const openMobileProductDetails = (
    product: Product,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => {
    setSelectedProduct(product);
    setDefaultOpenedTab(defaultOpenedTab);

    track({
      name: 'Details',
      props: {
        page: 'Catalog',
        id: product?._id,
        sku: product?.sku,
        name: product?.name,
        tab: defaultOpenedTab
      }
    });

    captureAnalytics(
      {
        productSKU: product.sku,
        actionType: ProductActionType.DetailsView,
        appType: appType || AnalyticsAppType.Mobile,
        ...(selectedCustomer
          ? {
              customerId: selectedCustomer._id
            }
          : {})
      },
      AnalyticsModules.Catalog
    );
  };

  useEffect(
    () => () => {
      if (mode === 'standalone') closeDialog('product-details');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onFeedbackSubmit = async ({ feedback, rating }: FeedbackValues) => {
    const isAi = searchType === SearchType.AI;

    const body = {
      _id: process.env.SLACK_CHANNEL_ID,
      type: isAi ? SlackMessageTypes.ProductMatch : SlackMessageTypes.Catalog,
      ...(rating ? { rating } : {}),
      feedback,
      user: {
        email: appUser?.Email,
        userId: appUser?.ID,
        company: appUser?.CompanyName,
        fullName: appUser?.FullName
      },
      results: {
        type: isAi ? aiType : 'search',
        data: isAi ? aiProducts : algoliaProducts,
        value: isAi ? aiSearch.value : searchQuery
      }
    };

    const res = await sendFeedback(body);

    if (!res) return;

    closeDialog(FEEDBACK_DIALOG);
    enqueueSnackbar('Feedback sent successfully', {
      variant: 'success'
    });
  };

  const newFacets = useMemo(() => {
    // We do not show anymore discountinued from sysco
    const syscoDisabled = [COMPANIES.SYSCO_DEV, COMPANIES.SYSCO_PROD].includes(
      appUser?.CompanyID
    )
      ? ['discontinued']
      : [];

    const updatedFacets = facets?.filter(
      f => !syscoDisabled?.includes(f?.name)
    );

    if (!visibleFacets?.length) return updatedFacets;

    const filtered = updatedFacets?.filter(f =>
      visibleFacets?.includes(f?.name)
    );

    return filtered;
  }, [facets, visibleFacets, appUser?.CompanyID]);

  if (invalidConfiguration) return <InvalidConfiguration />;

  if (isMobile)
    return (
      <MobileCatalog
        controller={controller}
        headerProps={{
          selectedCategory,
          feedback: (
            <SButton
              variant="text"
              onClick={() =>
                openDialog(
                  <Feedback
                    label={`How can we improve ${aiSearchActive ? 'AI Product Match' : 'Catalog'}?`}
                    onClose={() => closeDialog(FEEDBACK_DIALOG)}
                    handleSubmit={onFeedbackSubmit}
                    loading={isFeedbackLoading}
                  />,
                  {
                    id: FEEDBACK_DIALOG
                  }
                )
              }>
              Feedback
            </SButton>
          ),
          refetchProducts,
          handleSearch: val => {
            if (val.type === SearchType.AI) {
              handleAISearch(val.aiType, val.search);
            } else {
              searchProducts(val.search.value);
            }

            setSearchType(val.type);
            setSelectedCategory(null);
            handleFilter({
              manufacturer: [],
              stock: null,
              cost: []
            });
          },
          onSelectCategory: ctgId => {
            fetchProductsByCategory(ctgId);
            setAISearch({ value: '' });
            setSearchQuery('');
          }
        }}
        productsListProps={{
          statusId,
          products: aiSearchActive ? aiFilteredProducts : algoliaProducts,
          isLoading: isLoading || fetchMoreLoading || aiSearchLoading,
          filters: {
            customer: {
              show: configuration?.filters?.customer?.show,
              value: selectedCustomer,
              onChange: handleSelectCustomer
            },
            priceGroup: {
              show: configuration?.filters?.priceGroup?.show,
              value: selectedPriceGroup,
              onChange: handleSelectPriceGroup
            }
          },
          config: {
            columns: configuration.product?.columns,
            grid: configuration.product?.grid,
            priceType: configuration?.price,
            stock: configuration?.product?.grid?.stock,
            manufacturerSku: configuration?.product?.grid?.manufacturerSku
          },
          canShowActionBar: selected.length > 0 && canShowActionBar,
          disableSelect,
          productPricesLoading,
          searchQuery,
          selectedProducts: selected,
          drawerOpen,
          openProductDetails: openMobileProductDetails,
          fetchMore: fetchMoreProducts,
          onGetProductPrice: product =>
            updateFullPrice({
              productList: product,
              accountNumber: selectedCustomer.accountNumber
            }),
          setDrawerOpen,
          handleProductSelect: (isChecked: boolean, selectedP: Product[]) => {
            if (type === 'radio') {
              setSelected(selectedP);
              handleExport?.(selectedP);
            } else {
              handleSelect(isChecked, selectedP);

              const exported = isChecked
                ? [...selected, ...selectedP]
                : selected?.filter(s => s?._id !== selectedP?.[0]?._id);
              handleExport?.(exported);
            }
          },
          modifySelectedProduct: modifySelected
        }}
        categoriesProps={{
          selectedCategory,
          onSelectCategory: ctgId => {
            fetchProductsByCategory(ctgId);
            setAISearch({ value: '' });
            setSearchQuery('');
          }
        }}
        filtersProps={{
          globalSearch: searchQuery,
          selectedCategory,
          facets: newFacets,
          loading: isLoading,
          selectedFacets,
          attributesForFaceting,
          onSelectFacet
        }}
        productDetailsProps={{
          selectedProduct,
          selectedProducts: selected,
          selectedPriceGroup,
          selectedCustomerId: selectedCustomer?._id,
          defaultOpenedTab: defaultTab,
          userType,
          canSelectProducts: !disableSelect,
          onClose: () => setSelectedProduct(null),
          captureResourceAnalytics: additionalValues => {
            captureAnalytics(
              {
                appType,
                productSKU: selectedProduct.sku,
                actionType: ProductActionType.AssetView,
                ...(additionalValues || {})
              },
              AnalyticsModules.Catalog
            );
          },
          handleProductSelect: (checked, p) => {
            if (type === 'radio') {
              setSelected(p);
              handleExport?.(p);
            } else {
              handleSelect(checked, p);
              const exported = checked
                ? [...selected, ...p]
                : selected?.filter(s => s?._id !== p?.[0]?._id);
              handleExport?.(exported);
            }
          },
          openProductDetails: openMobileProductDetails,
          updateProduct: handleUpdateAlgoliaProduct,
          handleEmail,
          readOnly: mode === 'embedded'
        }}
      />
    );

  return (
    <Box display="flex" height="100%" flex={1}>
      <SQuadrantLayout
        defaultCollapsed={aiSearchActive}
        header={
          <Header
            searchQuery={aiSearchActive ? aiSearch : { value: searchQuery }}
            aiSearchActive={aiSearchActive}
            aiSearchType={aiType}
            loading={isLoading || aiSearchLoading}
            selectedCategory={selectedCategory}
            handleSearch={val => {
              if (val.type === SearchType.AI) {
                handleAISearch(val.aiType, val.search);
              } else {
                searchProducts(val.search.value);
              }

              setSearchType(val.type);
              setSelectedCategory(null);
              handleFilter({
                manufacturer: [],
                stock: null,
                cost: []
              });
            }}
            controller={controller}
            filters={{
              customer: {
                show: configuration?.filters?.customer?.show,
                value: selectedCustomer,
                onChange: handleSelectCustomer
              },
              priceGroup: {
                show: configuration?.filters?.priceGroup?.show,
                value: selectedPriceGroup,
                onChange: handleSelectPriceGroup
              }
            }}
            onSelectCategory={fetchProductsByCategory}
            refetchProducts={refetchProducts}
            onSearchClear={() => {
              setAISearch({ value: '' });
              setSearchQuery('');
            }}
            setSearchType={setSearchType}
            actions={
              <>
                {flags?.productsProductMatch && !aiSearchActive && (
                  <AskAiButton>
                    <SButton
                      variant="outlined"
                      onClick={() => setIsAISearchActive(true)}
                      startIcon={<IntelligenceIcon />}>
                      Ask AI
                    </SButton>
                  </AskAiButton>
                )}

                {/*
                <AIButton
                  newDesign
                  items={[
                    {
                      icon: (
                        <GradientIcon>
                          <ShuffleIcon fill="#fff" fontSize="small" />
                        </GradientIcon>
                      ),
                      text: 'Product Match',
                      flag: flags?.productsProductMatch,
                      onClick: () =>
                        openDialog(
                          <SProductMatch
                            options={[
                              SearchFormOptions.text,
                              SearchFormOptions.url,
                              SearchFormOptions.image
                            ]}
                            defaultType={SearchFormOptions.text}
                            onClose={() => closeDialog('product-match')}
                            handleSearch={(t, value) => {
                              handleAISearch(t, value);
                              headerSearchInputRef?.current?.setValue?.(value);
                              setSearchType(SearchType.AI);
                            }}
                            title="AI Product Match"
                            description="AI enables you to effortlessly find similar products within your catalog using various input methods."
                          />,
                          {
                            id: 'product-match',
                            preventClose: true
                          }
                        )
                    },
                    {
                      icon: (
                        <GradientIcon>
                          <RecommendationIcon
                            htmlColor="#fff"
                            style={{
                              width: 16,
                              height: 16
                            }}
                          />
                        </GradientIcon>
                      ),
                      text: 'Product Finder',
                      flag: flags?.equipmentFinder,
                      onClick: () =>
                        openDialog(
                          <SEquipmentFinder
                            handleSearch={(t, value) => {
                              handleAISearch(t, { value, text: value });
                              setSearchType(SearchType.AI);
                            }}
                            onClose={() => closeDialog('product-match')}
                          />,
                          {
                            id: 'product-match',
                            preventClose: true
                          }
                        )
                    },
                    {
                      icon: (
                        <GradientIcon>
                          <AIIcon fill="#fff" />
                        </GradientIcon>
                      ),
                      text: 'Product Enhancer',
                      flag: flags?.productEnhancer,
                      onClick: onProductEnhancerMenuItemClick
                    },
                    {
                      icon: <IntelligenceIcon />,
                      text: 'Ask AI',
                      flag: flags?.aiSearch,
                      onClick: () => setIsAISearchActive(true)
                    }
                  ]}
                />
                */}
                {flags?.catalogPriceLister && selectedCustomer && (
                  <PriceLister
                    accountNumber={selectedCustomer?.accountNumber}
                  />
                )}
              </>
            }
          />
        }
        bottomLeft={
          <BottomLeftWrapper
            categoriesProps={{
              selectedCategory,
              onSelectCategory: ctgId => {
                fetchProductsByCategory(ctgId);
                setAISearch({ value: '' });
                setSearchQuery('');
              }
            }}
            filtersProps={{
              globalSearch: searchQuery,
              onSelectFacet,
              selectedCategory,
              facets: newFacets,
              loading: isLoading,
              selectedFacets,
              attributesForFaceting
            }}
          />
        }
        bottomRight={
          <>
            {aiSearchActive && !!aiProducts.length && (
              <Box>
                {urlDescription && aiType !== SearchFormOptions.text && (
                  <Box mb={distances.px.base}>
                    <InfoContainer
                      prefix={`${aiType === SearchFormOptions.image ? 'Image' : 'URL'} Description:`}
                      text={urlDescription}
                    />
                  </Box>
                )}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  pl={2}
                  mb={distances.px.base}
                  alignItems="center">
                  <SText
                    lineHeight="24px"
                    weight="semibold"
                    title={`AI Product Recommendation (${aiFilteredProducts.length})`}
                  />
                  <AIProductsFilters
                    products={aiProducts}
                    showStockFilter={configuration?.product?.grid?.stock.show}
                    selectedFilters={selectedFilters}
                    loading={aiSearchLoading}
                    onChange={handleFilter}
                  />
                </Box>
              </Box>
            )}
            <ProductsList
              type={type}
              statusId={statusId}
              products={aiSearchActive ? aiFilteredProducts : algoliaProducts}
              isLoading={isLoading || aiSearchLoading}
              aiSearchActive={aiSearchActive}
              config={{
                columns: configuration.product?.columns,
                grid: configuration.product?.grid,
                priceType: configuration?.price,
                stock: configuration?.product?.grid?.stock,
                manufacturerSku: configuration?.product?.grid?.manufacturerSku
              }}
              feedback={
                <FeedbackContainer>
                  <FeedbackButton
                    variant="text"
                    startIcon={
                      <ThumbsUpIcon fill="inherit" style={{ width: 18 }} />
                    }
                    onClick={() =>
                      openDialog(
                        <Feedback
                          label={`How can we improve ${aiSearchActive ? 'AI Product Match' : 'Catalog'}?`}
                          onClose={() => closeDialog(FEEDBACK_DIALOG)}
                          handleSubmit={onFeedbackSubmit}
                          loading={isFeedbackLoading}
                        />,
                        {
                          id: FEEDBACK_DIALOG
                        }
                      )
                    }>
                    Give Feedback
                  </FeedbackButton>
                </FeedbackContainer>
              }
              canShowActionBar={selected.length > 0 && canShowActionBar}
              selectedCustomer={selectedCustomer}
              selectedPriceGroup={selectedPriceGroup}
              disableSelect={disableSelect}
              productPricesLoading={productPricesLoading}
              searchQuery={searchQuery}
              selectedProducts={selected}
              composeEmailValues={values}
              drawerOpen={drawerOpen}
              fetchMore={fetchMoreProducts}
              onPromotionClick={handlePromotion}
              openProductDetails={openProductDetails}
              updateProduct={handleUpdateAlgoliaProduct}
              onGetProductPrice={product =>
                updateFullPrice({
                  productList: product,
                  accountNumber: selectedCustomer.accountNumber
                })
              }
              setDrawerOpen={setDrawerOpen}
              handleProductSelect={(
                isChecked: boolean,
                selectedP: Product[]
              ) => {
                if (type === 'radio') {
                  setSelected(selectedP);
                  handleExport?.(selectedP);
                } else {
                  handleSelect(isChecked, selectedP);

                  const exported = isChecked
                    ? [...selected, ...selectedP]
                    : selected?.filter(s => s?._id !== selectedP?.[0]?._id);
                  handleExport?.(exported);
                }
              }}
              modifySelectedProduct={modifySelected}
              handleEmail={handleEmail}
            />
          </>
        }
      />
      <Dialog
        maxWidth="md"
        id="product-details"
        aria-labelledby="simple-dialog-title"
        open={selectedProduct && dialogOpened}
        style={{ zIndex: '1300' }}
        onClose={() => setDialogOpened(false)}
        onKeyDown={event => {
          if (event.key === 'Escape') setDialogOpened(false);
        }}
        classes={{ paper: classes.dialogPaper }}>
        <ProductDetails
          selectedProduct={selectedProduct}
          selectedProducts={selected}
          selectedPriceGroup={selectedPriceGroup}
          selectedCustomerId={selectedCustomer?._id}
          defaultOpenedTab={defaultTab}
          userType={userType}
          handleProductSelect={(checked, p) => {
            if (type === 'radio') {
              setSelected(p);
              handleExport?.(p);
            } else {
              handleSelect(checked, p);
              const exported = checked
                ? [...selected, ...p]
                : selected?.filter(s => s?._id !== p?.[0]?._id);
              handleExport?.(exported);
            }
          }}
          canSelectProducts={!disableSelect}
          captureResourceAnalytics={additionalValues => {
            captureAnalytics(
              {
                appType,
                productSKU: selectedProduct.sku,
                actionType: ProductActionType.AssetView,
                ...(additionalValues || {})
              },
              AnalyticsModules.Catalog
            );
          }}
          openProductDetails={openProductDetails}
          onClose={() => setDialogOpened(!dialogOpened)}
          updateProduct={handleUpdateAlgoliaProduct}
          handleEmail={handleEmail}
          handleSelected={() => setDrawerOpen(!drawerOpen)}
          handleCloseActionBar={() => handleSelect(false, selected)}
          readOnly={mode === 'embedded'}
        />
      </Dialog>
    </Box>
  );
}
